import type { ComponentProps, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { TextSwitch } from '@sorare/core/src/atoms/inputs/TextSwitch';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';

import { SelectRarity } from 'atoms/SelectRarity';

const Label = styled(Horizontal)`
  align-items: center;
  padding: 0 var(--half-unit);
`;

type Props<T extends Rarity> = {
  rarities:
    | T[]
    | {
        badgeContent?: ReactNode;
        value: T;
      }[];
  selectedRarity: T;
  onChange: (rarity: T) => void;
  withoutLabel?: boolean;
  transparent?: boolean;
  variant?: ComponentProps<typeof TextSwitch>['variant'];
  buttonSize?: ComponentProps<typeof SelectRarity>['buttonSize'];
};

export const TextSwitchRarity = <T extends Rarity>({
  rarities: raritiesProps,
  selectedRarity,
  onChange,
  withoutLabel = false,
  transparent = true,
  variant = 'primary',
  buttonSize = 'compact',
}: Props<T>) => {
  const rarities = raritiesProps.map(r =>
    typeof r === 'object' ? r : { value: r, badgeContent: undefined }
  );
  const { up: upToTablet } = useScreenSize('tablet');
  if (upToTablet) {
    return (
      <TextSwitch
        options={rarities.map(({ value: r, badgeContent }) => ({
          label: (
            <Badge badgeContent={badgeContent} size="small">
              <Label as="span">
                <ScarcityIcon rarity={r} size={2} />
                {!withoutLabel && selectedRarity === r && (
                  <FormattedMessage {...scarcityMessages[r]} />
                )}
              </Label>
            </Badge>
          ),
          value: r,
        }))}
        value={selectedRarity}
        onChange={onChange}
        onlyGrowOnSelected
        transparent={transparent}
        variant={variant}
        compact
      />
    );
  }
  return (
    <SelectRarity
      rarities={rarities}
      selectedRarity={selectedRarity}
      onChange={onChange}
      buttonSize={buttonSize}
    />
  );
};
